import React from "react";
import { AFFILIATE_PRODUCTS } from "../constants.js";
import { AffiliateProduct } from "../types.js";
// import AffiliateDisclosure from '../components/AffiliateDisclosure.js';
import SEOMeta from "../components/SEOMeta.js";

const ToolkitPage: React.FC = () => {
  // ========== SAFE ACCESSOR FUNCTIONS ==========
  const safeGet = <T,>(value: T | undefined | null, fallback: T): T => {
    return value !== undefined && value !== null ? value : fallback;
  };

  const safeGetNumber = (value: number | undefined | null, fallback: number): number => {
    return typeof value === 'number' && !isNaN(value) ? value : fallback;
  };
 
  // ========== CATEGORIES & PRODUCTS ==========
  const categories = [
    { id: "vpn", label: "VPN", icon: "fa-shield-vpn" },
    { id: "antivirus", label: "Antivirus", icon: "fa-shield-halved" },
    { id: "hardware", label: "Hardware", icon: "fa-microchip" },
    { id: "password-manager", label: "Password Manager", icon: "fa-key" },
    { id: "backup", label: "Backup", icon: "fa-hard-drive" },
    { id: "firewall", label: "Firewall", icon: "fa-fire" },
    { id: "monitoring", label: "Monitoring", icon: "fa-chart-line" },
    { id: "training", label: "Training", icon: "fa-graduation-cap" },
  ];

  const getProductsByCategory = (categoryId: string): AffiliateProduct[] => {
    // For broader matching, check if category label matches product category
    const category = categories.find(c => c.id === categoryId);
    if (!category) return [];
    
    return AFFILIATE_PRODUCTS.filter((product) => {
      // Check for exact match or partial match
      const productCategory = (product.category || "").toLowerCase();
      const searchTerm = category.label.toLowerCase();
      
      return productCategory.includes(searchTerm) || 
             searchTerm.includes(productCategory) ||
             // For "password manager" matching "password"
             (searchTerm.includes("password") && productCategory.includes("password"));
    });
  };

  const getSortedProducts = (products: AffiliateProduct[]): AffiliateProduct[] => {
    return [...products].sort((a, b) => {
      // First sort by top pick
      if (a.isTopPick && !b.isTopPick) return -1;
      if (!a.isTopPick && b.isTopPick) return 1;
      
      // Then by rating
      const ratingA = safeGetNumber(a.rating, 0);
      const ratingB = safeGetNumber(b.rating, 0);
      return ratingB - ratingA;
    });
  };

  const renderRatingStars = (rating: number = 0, size: 'sm' | 'md' = 'md') => {
    const maxStars = 5;
    const starSize = size === 'sm' ? 'text-[10px]' : 'text-sm';
    
    return (
      <div className="flex items-center gap-1">
        {[...Array(maxStars)].map((_, i) => (
          <i
            key={i}
            className={`fa-solid fa-star ${starSize} ${
              i < Math.floor(rating) ? "text-amber-500" : "text-slate-300 dark:text-slate-700"
            }`}
          ></i>
        ))}
        <span className={`ml-1 font-bold ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const renderProductCard = (product: AffiliateProduct, index: number) => {
    const productName = safeGet(product.name, "Security Product");
    const productDescription = safeGet(product.description, "Professional-grade security solution");
    const productRating = safeGetNumber(product.rating, 4.0);
    const productUrl = safeGet(product.affiliateUrl, "#");
    const productPrice = safeGet(product.priceTag, "Starting at $XX.XX");
    const isTopPick = safeGet(product.isTopPick, false);
    
    return (
      <div
        key={product.id || productName}
        className={`flex flex-col sm:flex-row items-stretch gap-4 sm:gap-6 p-4 sm:p-6 ${
          index % 2 === 0
            ? "bg-white dark:bg-slate-800/30"
            : "bg-slate-50 dark:bg-slate-900/30"
        } group hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-300 border-b border-slate-100 dark:border-slate-800 last:border-b-0`}
      >
        {/* Rank Badge */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-lg sm:rounded-xl flex items-center justify-center text-lg sm:text-xl font-black italic shadow-lg group-hover:scale-105 transition-transform">
            #{index + 1}
          </div>
        </div>

        {/* Product Info */}
        <div className="flex-grow space-y-2 sm:space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-start gap-2">
            <div className="flex-grow">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h3 className="text-lg sm:text-xl font-black uppercase tracking-tighter text-slate-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {productName}
                </h3>
                {isTopPick && (
                  <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[8px] sm:text-[9px] font-black rounded-full uppercase tracking-widest shadow-sm">
                    TOP PICK
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                {productDescription}
              </p>
            </div>
          </div>

          {/* Tags & Details */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {product.targetAttackTypes && product.targetAttackTypes.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {product.targetAttackTypes.slice(0, 2).map((type, idx) => (
                  <span
                    key={idx}
                    className="text-[8px] sm:text-[9px] text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700"
                  >
                    {type}
                  </span>
                ))}
                {product.targetAttackTypes.length > 2 && (
                  <span className="text-[8px] sm:text-[9px] text-slate-400">
                    +{product.targetAttackTypes.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Price & Rating */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <span className="text-blue-600 dark:text-blue-400 font-black text-base sm:text-lg">
                {productPrice}
              </span>
              <div className="hidden sm:block">
                {renderRatingStars(productRating, 'sm')}
              </div>
            </div>
            <div className="sm:hidden">
              {renderRatingStars(productRating, 'sm')}
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="flex-shrink-0 self-center sm:self-stretch flex flex-col justify-center">
          <a
            href={productUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-black text-[10px] sm:text-[11px] uppercase tracking-wider rounded-lg shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transition-all flex items-center justify-center gap-2 min-w-[120px]"
          >
            <span>Visit Site</span>
            <i className="fa-solid fa-arrow-up-right-from-square text-[8px]"></i>
          </a>
          {product.rating && product.rating >= 4.5 && (
            <p className="text-[8px] text-slate-500 dark:text-slate-400 text-center mt-1">
              Highest Rated
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderEmptyState = (category: { label: string; icon: string }) => (
    <div className="bg-slate-50 dark:bg-slate-900/30 rounded-xl sm:rounded-2xl p-8 sm:p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-800">
      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className={`fa-solid ${category.icon} text-2xl text-slate-400 dark:text-slate-600`}></i>
      </div>
      <h3 className="text-lg font-black text-slate-700 dark:text-slate-300 mb-2">
        No {category.label} Products Yet
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
        We're currently evaluating {category.label.toLowerCase()} solutions. Check back soon for expert recommendations.
      </p>
    </div>
    
  );
 
  const canonicalUrl = "https://cyberannals.com/toolkit";
  return (
    <>
      <SEOMeta
        title="Cyber Security Defense Toolkit | Top-Rated Security Tools"
        description="Expert-reviewed cybersecurity tools and software to protect against historical attack patterns. Compare different security solutions."
        keywords="cybersecurity tools, best security software, antivirus comparison, VPN reviews, password managers, hardware security, security toolkit 2024"
        canonical={canonicalUrl}
        ogType="website"
        articleSection="Security Tools"
        articleTags={["security tools", "cybersecurity", "software reviews", "protection"]}
      />
      
      <div className="space-y-12 sm:space-y-16 py-6 sm:py-12 animate-in fade-in duration-700 max-w-7xl mx-auto px-4">
        {/* HEADER */}
        <div className="text-center space-y-6 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.3em] text-blue-600 dark:text-blue-400">
            <i className="fa-solid fa-shield-halved"></i>
            <span>DEFENSE TOOLKIT</span>
            <i className="fa-solid fa-shield-halved"></i>
          </div>
          
          <h1 className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter uppercase italic leading-none text-slate-900 dark:text-white">
            Security Arsenal
          </h1>
          
          <div className="h-1 w-24 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full mx-auto"></div>
          
          <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-lg font-medium max-w-2xl mx-auto">
            Professional-grade tools curated from historical attack analysis to prevent breaches on your network.
          </p>
          
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-6">
            {categories.map((cat) => {
              const productCount = getProductsByCategory(cat.id).length;
              if (productCount === 0) return null;
              
              return (
                <a
                  key={cat.id}
                  href={`#${cat.id}`}
                  className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-bold text-slate-700 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-400 transition-all flex items-center gap-2"
                >
                  <i className={`fa-solid ${cat.icon} text-[10px]`}></i>
                  <span>{cat.label}</span>
                  <span className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded">
                    {productCount}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        {/* TOOL CATEGORIES */}
        <div className="space-y-16 sm:space-y-24">
          {categories.map((category) => {
            const products = getProductsByCategory(category.id);
            const sortedProducts = getSortedProducts(products);
            
            if (sortedProducts.length === 0) return null;
            
            return (
              <section 
                key={category.id} 
                id={category.id}
                className="scroll-mt-24"
              >
                <div className="flex items-center gap-4 mb-8 sm:mb-10">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                    <i className={`fa-solid ${category.icon} text-white text-lg`}></i>
                  </div>
                  <div className="flex-grow">
                    <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tighter italic text-slate-900 dark:text-white">
                      {category.label} Solutions
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {sortedProducts.length} expert-reviewed {category.label.toLowerCase()} {sortedProducts.length === 1 ? 'solution' : 'solutions'}
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl sm:rounded-2xl border border-slate-200 dark:border-slate-800 shadow-lg">
                  {sortedProducts.map((product, index) => 
                    renderProductCard(product, index)
                  )}
                </div>

                {sortedProducts.length === 0 && renderEmptyState(category)}
              </section>
            );
          })}
        </div>

        {/* METHODOLOGY SECTION */}
        <section className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-900/50 dark:to-slate-950 p-6 sm:p-10 rounded-2xl sm:rounded-3xl border border-slate-200 dark:border-slate-800 mt-16">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 text-blue-700 dark:text-blue-400 text-xs font-black uppercase tracking-wider rounded-full border border-blue-500/20">
              <i className="fa-solid fa-clipboard-check"></i>
              SELECTION METHODOLOGY
            </div>
            
            <h3 className="text-2xl font-black text-slate-900 dark:text-white">
              How We Choose Security Tools
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
              <div className="space-y-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <i className="fa-solid fa-shield-halved text-blue-600 dark:text-blue-400"></i>
                </div>
                <h4 className="font-bold text-slate-900 dark:text-white">Historical Efficacy</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Tools are evaluated based on their effectiveness against historical attack patterns documented in our database.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <i className="fa-solid fa-chart-line text-blue-600 dark:text-blue-400"></i>
                </div>
                <h4 className="font-bold text-slate-900 dark:text-white">Independent Testing</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Products undergo rigorous testing against real-world threats and attack simulations.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <i className="fa-solid fa-users text-blue-600 dark:text-blue-400"></i>
                </div>
                <h4 className="font-bold text-slate-900 dark:text-white">Expert Community</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Recommendations are validated by cybersecurity professionals and incident responders.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* DISCLOSURE FOOTER */}
        <div className="pt-8 border-t border-slate-200 dark:border-slate-800">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              <i className="fa-solid fa-circle-info mr-1"></i>
              {/* Disclosure: CyberAnnals may earn commissions from qualifying purchases through affiliate links. 
              This supports our independent security research. */}
            </p>
            <p className="text-[10px] text-slate-400 dark:text-slate-600 uppercase tracking-wider">
              {/* Prices and availability subject to change. Last updated: {new Date().toLocaleDateString()} */}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ToolkitPage;